import * as React from "react";
import {
  TextField,
  Grid,
  Box,
  Typography,
  Container,
  Alert,
  IconButton,
  useMediaQuery,
  Link,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ColorModeContext, tokens } from "../../theme";
import {
  DarkModeOutlined,
  Grading,
  LightModeOutlined,
  LocalShippingOutlined,
} from "@mui/icons-material";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import Footer from "../common/Footer";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import { login } from "../../redux/slice/authSlice";
import {
  removeItemFromLocalStorage,
  setDataToLocalStorage,
  getDataFromLocalStorage,
} from "../../utils/StorageUtils";
import Checkbox from "../common/Checkbox";
import CustomPasswordTextField from "../common/CustomPasswordTextField";
import { useForm } from "react-hook-form";
import { FAILED_CODE } from "../../service/ServiceConstants";
import { hideMainErrorPopup } from "../../redux/slice/commonSlice";
import { fetchUserPreference } from "../../redux/slice/userSlice";
import { useNavigation } from "../../hooks/useNavigation";
import { useBranding } from "../../BrandingContext";
import TroubleSignin from "./TroubleSignin";
import HIPAASeal from "../../assets/Seal_HIPAA.svg";
// import RoleConfirmationPopup from "../operation/account/RoleConfirmationDialog";
import Card from "../common/Card";
import RoleConfirmationPopup from "../operation/account/RoleConfirmationPopup";
import ContactUsToGetStartedPopup from "../operation/account/ContactUsToGetStartedPopup";

export default function SignIn() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = React.useContext(ColorModeContext);
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [errorMessageContent, setErrorMessageContent] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [rememberMe, setRememberMe] = React.useState(false);
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const { control } = useForm();
  const [isEmailAutofilled, setIsEmailAutofilled] = React.useState(false);
  const [isPasswordAutofilled, setIsPasswordAutofilled] = React.useState(false);
  const [openTroubleSignin, setOpenTroubleSignin] = React.useState(false);

  const [openYourRoleConfirmPopup, setOpenYourRoleConfirmPopup] =
    React.useState(false);
  
  const [openContactUsPopup, setOpenContactUsPopup] = React.useState(false); // CS-1865

  const branding = useBranding();

  React.useEffect(() => {
    const savedUsername = getDataFromLocalStorage("savedUsername");
    const savedRememberMe = getDataFromLocalStorage("rememberMe") === "true";
    if (savedRememberMe && savedUsername) {
      setUsername(savedUsername);
      setRememberMe(savedRememberMe);
    }
  }, []);

  const handleClose = () => setOpen(false);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email: string = data.get("email") as string;
    const password: string = data.get("password") as string;
    handleLogin(email, password);
  };

  const handleLogin = async (userName: string, password: string) => {
    if (userName && userName.trim() !== "" && password) {
      userName = userName.trim();
      setUsername(userName);
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userName)) {
        setErrorMessageContent("Invalid email address.");
        setOpen(true);
        return;
      }
      try {
        const response = await dispatch(
          login({
            loginInfo: { userName, password },
            interceptorOption: { skipErrorPopup: true },
          }),
        ).unwrap();

        localStorage.setItem("login", Date.now().toString());

        if (rememberMe) {
          setDataToLocalStorage("savedUsername", userName);
          setDataToLocalStorage("rememberMe", "true");
        } else {
          removeItemFromLocalStorage("savedUsername");
          removeItemFromLocalStorage("rememberMe");
        }

        if (response) {
          dispatch(fetchUserPreference(response.userId)).then((res) => {
            // const preferences = res.payload;   // CS-1984: guarded below
            const preferences = res.payload ?? [];     // CS-1984
            const preferredTheme = preferences.find(
              (pref: { preferenceName: string }) =>
                pref.preferenceName === "preferredTheme",
            );
            if (
              preferredTheme &&
              theme.palette.mode !== preferredTheme.preferenceValue
            ) {
              colorMode.toggleColorMode();
            }
          });
        }
        dispatch(hideMainErrorPopup());
      } catch (e: any) {
        setErrorMessageContent(
          e?.code === FAILED_CODE
            ? e?.message
            : "Unknown error. Please contact support.",
        );
      }
    } else {
      setErrorMessageContent("Please enter both email and password.");
    }
    setOpen(true);
  };

  React.useLayoutEffect(() => {
    const checkAutofilled = () => {
      setIsEmailAutofilled(
        !!document.getElementById("email")?.matches("*:-webkit-autofill"),
      );
      setIsPasswordAutofilled(
        !!document.getElementById("password")?.matches("*:-webkit-autofill"),
      );
    };
    setTimeout(checkAutofilled, 200);
    setTimeout(checkAutofilled, 1000);
    setTimeout(checkAutofilled, 2000);
  }, []);

  React.useEffect(() => {
    if (isEmailAutofilled) setIsEmailAutofilled(false);
    if (isPasswordAutofilled) setIsPasswordAutofilled(false);
  }, [username, password]);

  // CS-1865 - Start
  const handleConfirmOtherRole = () => {
    setOpenYourRoleConfirmPopup(false);
    setOpenContactUsPopup(true);
  }
  // CS-1865 - End

  const handleOpenYourRoleConfirmPopup = () => {
    if (openYourRoleConfirmPopup) {
      navigate("/create_account_step");
    }
    setOpenYourRoleConfirmPopup(!openYourRoleConfirmPopup);
  };

   const handleCloseYourRoleConfirmPopup = () => {
    setOpenYourRoleConfirmPopup(false);
  };

  const features = [
    ...(branding?.companyNameShort === "WoundWave"
      ? [
        {
          icon: <Grading sx={{ fontSize: 28, color: "#4db8ac" }} />,
          title: "Direct Ordering",
          description: "Place requests and get quotes online, bypassing reps.",
        },
      ]
      : []),
    {
      icon: (
        <AssignmentTurnedInOutlinedIcon
          sx={{ fontSize: 28, color: "#4db8ac" }}
        />
      ),
      title: "Insurance & Authorization Workflows",
      description:
        "Real-time eligibility checks, documentation checkpoints, and visibility into status—so nothing ships without coverage confirmed.",
    },
    {
      icon: <Inventory2OutlinedIcon sx={{ fontSize: 28, color: "#4db8ac" }} />,
      title: "Unified Product Procurement",
      description:
        "One catalog, countless brands. Streamlined contracting, ordering, shipment tracking, and inventory visibility all in one platform.",
    },
    {
      icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 28, color: "#4db8ac" }} />,
      title: "Invoice & Payment Reconciliation",
      description:
        "Track invoices tied to orders, see clear payment status, and even pay invoices right from the platform.",
    },
    {
      icon: (
        <VerifiedUserOutlinedIcon sx={{ fontSize: 28, color: "#4db8ac" }} />
      ),
      title: "Built-in Compliance",
      description:
        "Automated audit trails and guideline-based workflows to help keep your practice aligned with compliance.",
    },
  ];

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
        }}
      >
        <Grid container sx={{ height: "100vh" }}>
          <Grid
            item
            xs={12}
            md={6.5}
            sx={{
              display: { xs: "none", md: "flex" },
              background:
                theme.palette.mode === "dark"
                  ? "linear-gradient(90deg, #0f2027 0%, #203a43 100%)"
                  : "linear-gradient(90deg, #f5f9fb 0%, #e9f1f4 100%)",
              flexDirection: "column",
              px: { sm : 3, md: 6, lg: 12 },
              py: { md: 6 },
              position: "relative",
            }}
          >
            <Box sx={{ mb: 3 }}>
              <img
                alt="V3 BIOMED logo"
                src={
                  branding?.logoWhite ||
                  require("../../branding-assets/v3biomed/logo.png")
                }
                style={{ height: 72, objectFit: "contain" }}
              />
            </Box>

            {/* Content */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                width: "100%", // ✅ full width
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: "3rem",
                  lineHeight: 1.15,
                  mb: 2,
                }}
              >
                The Unified Wound Care Platform
              </Typography>

              {/* MAIN DESCRIPTION — FLUSH */}
              <Typography
                sx={{
                  mb: 3,
                  color: colors.grey[500],
                  lineHeight: 1.65,
                  fontSize: "1.05rem",
                  textAlign: "justify", // ✅ flush both sides
                  textJustify: "inter-word",
                }}
              >
                Streamline procurement, verification, invoice management, and
                workflow execution within a single platform, with a robust wound
                care portfolio spanning from standard and conservative care
                through advanced therapies.
              </Typography>

              {/* Features */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {features.map((feature, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        backgroundColor: "rgba(77, 184, 172, 0.12)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mt: "2px",
                      }}
                    >
                      {React.cloneElement(feature.icon, {
                        sx: { fontSize: 22, color: colors.grey[400] },
                      })}
                    </Box>

                    {/* FEATURE TEXT — FLUSH */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          mb: 0.3,
                        }}
                      >
                        {feature.title}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.grey[500],
                          lineHeight: 1.6,
                          textAlign: "justify", // ✅ flush
                          textJustify: "inter-word",
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Divider */}
              <Box
                sx={{
                  height: "1px",
                  width: "100%",
                  background:
                    "linear-gradient(to right, transparent, rgba(168,197,205,0.35), transparent)",
                  my: 5,
                }}
              />

              {/* Stats */}
              <Box sx={{ display: "flex", gap: 4, mb: 3, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocalShippingOutlined
                    sx={{ fontSize: 18, color: colors.grey[500] }}
                  />
                  <Typography variant="body2" sx={{ color: colors.grey[500] }}>
                    Fewer handoffs
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DescriptionOutlinedIcon
                    sx={{ fontSize: 18, color: colors.grey[500] }}
                  />
                  <Typography variant="body2" sx={{ color: colors.grey[500] }}>
                    Fewer denials
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SpeedOutlinedIcon
                    sx={{ fontSize: 18, color: colors.grey[500] }}
                  />
                  <Typography variant="body2" sx={{ color: colors.grey[500] }}>
                    Faster fulfillment
                  </Typography>
                </Box>
              </Box>

              {/* Footer */}
              <Typography
                variant="body2"
                sx={{
                  color: "#718a92",
                  fontSize: "0.9rem",
                }}
              >
                Trusted by wound care practices who demand operational clarity.
              </Typography>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={5.5}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pt: { xs: 4, md: 6 },
              position: "relative",
            }}
          >
            <IconButton
              onClick={colorMode.toggleColorMode}
              sx={{
                position: "absolute",
                top: 20,
                right: 20,
              }}
            >
              {theme.palette.mode === "dark" ? (
                <LightModeOutlined />
              ) : (
                <DarkModeOutlined />
              )}
            </IconButton>

            <Box sx={{ width: "100%", maxWidth:{ xs: "320px" , md: "420px"  } }}>
              {/* Mobile Logo - Only visible on mobile */}
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  justifyContent: "center",
                  mb:2
                }}
              >
                <img
                  alt="V3 BIOMED logo"
                  src={require("../../branding-assets/v3biomed/logo.png")
                  }
                  style={{ height: 50, objectFit: "contain" }}
                />
              </Box>

              <Card
                sx={{
                  borderRadius: 3,
                  p: 4,
                  mb: 3,
                }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  mb={4}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Welcome Back
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.grey[500],
                    }}
                  >
                    Sign in to access your V3 Biomed dashboard
                  </Typography>
                </Box>

                <Box component="form" onSubmit={handleSubmit} noValidate>
                  {errorMessageContent && open && (
                    <Box mb={2}>
                      <Alert
                        variant="filled"
                        severity="error"
                        onClose={handleClose}
                      >
                        {errorMessageContent}
                      </Alert>
                    </Box>
                  )}

                  <Box mb={3}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        fontWeight: 500,
                      }}
                    >
                      Email
                    </Typography>
                    <TextField
                      fullWidth
                      id="email"
                      placeholder="Enter your email"
                      name="email"
                      autoComplete="email"
                      color={
                        theme.palette.mode === "dark" ? "secondary" : "primary"
                      }
                      style={{
                        borderRadius: "0",
                      }}
                      InputProps={{ sx: { borderRadius: 2 } }}
                      InputLabelProps={{
                        shrink: isEmailAutofilled || username != "",
                      }}
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                    />
                  </Box>

                  <Box mb={2}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        fontWeight: 500,
                      }}
                    >
                      Password
                    </Typography>
                    <CustomPasswordTextField
                      control={control}
                      margin="normal"
                      fullWidth
                      controllerName="password"
                      placeholder="Enter your password"
                      id="password"
                      autoComplete="current-password"
                      sx={{ borderRadius: 2, marginTop: 0 }}
                      InputLabelProps={{
                        shrink: isPasswordAutofilled || password != "",
                      }}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </Box>

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                  >
                    <Checkbox
                      value={rememberMe}
                      color="primary"
                      label="Remember me"
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      sx={{
                        cursor: "pointer",
                        color: colors.grey[100],
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/forgot-password");
                      }}
                    >
                      Forgot password?
                    </Link>
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{
                      borderRadius: 1.5,
                      mb: 2,
                      backgroundColor:  theme.palette.mode === "dark" ? "#169c9c" : "#c6ebeb",
                      color: colors.grey[100],
                      fontWeight: 500,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: theme.palette.mode === "dark" ? "#169c9ce6" : "#b6e0e0",
                      },
                    }}
                  >
                    Sign In
                  </Button>

                  <Box textAlign="center" mb={3}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      sx={{
                        cursor: "pointer",
                        color: colors.grey[500],
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenTroubleSignin(true);
                      }}
                    >
                      Trouble signing in?
                    </Link>
                  </Box>

                  <Box
                    sx={{
                      borderTop: "1px solid rgba(168, 197, 205, 0.2)",
                      pt: 3,
                      textAlign: "center",
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="large"
                      fullWidth
                      sx={{
                        borderRadius: 1.5,
                        mb: 2,
                        borderColor: colors.grey[700],
                        color: colors.grey[100],
                        fontWeight: 500,
                        textTransform: "none",
                        "&:hover": {
                          borderColor: colors.grey[600],
                          color: colors.grey[800],
                          backgroundColor: "#30e8e8",
                        },
                      }}
                      onClick={handleOpenYourRoleConfirmPopup}
                    >
                      Create Account →
                    </Button>
                    {/* <Button
                      variant="outlined"
                      size="large"
                      fullWidth
                      sx={{
                        borderRadius: 1.5,
                        mb: 2,
                        borderColor: colors.grey[700],
                        color: colors.grey[100],
                        fontWeight: 500,
                        textTransform: "none",
                        "&:hover": {
                          borderColor: colors.grey[600],
                          color: colors.grey[800],
                          backgroundColor: "#30e8e8",
                        },
                      }}
                      onClick={handleOpenYourRoleConfirmPopup}
                    >
                      New Account →
                    </Button> */}
                  </Box>
                </Box>
              </Card>

              {/* HIPAA Seal */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 3,
                }}
              >
                <img
                  src={HIPAASeal}
                  alt="HIPAA Compliant"
                  style={{
                    height: "90px",
                    animation: "floatUpDown 3s ease-in-out infinite",
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          textAlign: "center",
          py: 1.5,
          zIndex: 1100,
        }}
      >
        <Footer />
      </Box>

      {/* Modals */}
      <TroubleSignin
        open={openTroubleSignin}
        onClose={() => setOpenTroubleSignin(false)}
      />

      <RoleConfirmationPopup
        open={openYourRoleConfirmPopup}
        onClose={handleCloseYourRoleConfirmPopup}
        onConfirmMedicalRole={handleOpenYourRoleConfirmPopup}
        onConfirmOtherRole={handleConfirmOtherRole} // CS-1865
      />
      {/* CS-1865 - Start */}
      <ContactUsToGetStartedPopup
        open={openContactUsPopup}
        onClose={() => setOpenContactUsPopup(false)}
      />
      {/* CS-1865 - End */}

      <style>
        {`
          @keyframes floatUpDown {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-8px); }
            100% { transform: translateY(0px); }
          }
        `}
      </style>
    </>
  );
}